import React, { useState, memo } from 'react';
import { Check, ChevronDown, ShieldCheck } from 'lucide-react';
import { FadeIn } from './ui/FadeIn';
import { Button } from './ui/Button';
import { useCurrency } from '../contexts/CurrencyContext';
export function OfferStack() {
  const {
    currency,
    formatPrice,
    isLoading
  } = useCurrency();
  const [faqOpen, setFaqOpen] = useState(false);
  return <section id="offer" className="py-20 md:py-28 px-6 bg-base relative overflow-hidden" data-id="element-426">
      {/* Subtle background glow behind the bundle */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent-primary/5 rounded-full blur-[120px] pointer-events-none" data-id="element-427" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-428">
        {/* Section label */}
        <FadeIn data-id="element-429">
          <div className="flex items-center justify-center gap-4 mb-12" data-id="element-430">
            <div className="h-px bg-border-accent w-12 md:w-24" data-id="element-431" />
            <span className="font-ui text-xs font-semibold tracking-widest uppercase text-accent-primary" data-id="element-432">
              ◆ The Offer ◆
            </span>
            <div className="h-px bg-border-accent w-12 md:w-24" data-id="element-433" />
          </div>
        </FadeIn>

        {/* Headlines */}
        <FadeIn delay={0.1} data-id="element-434">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary mb-6 text-center leading-tight" data-id="element-435">
            Choose How You Experience the Story
          </h2>
          <p className="font-body text-xl text-text-secondary text-center mb-16 max-w-2xl mx-auto leading-relaxed" data-id="element-436">
            Read it. Listen to it. Or own the complete memorial collection.
          </p>
        </FadeIn>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center max-w-5xl mx-auto mb-16" data-id="element-437">
          {/* Card 1: Digital Edition */}
          <FadeIn delay={0.2} className="h-full" data-id="element-438">
            <div className="bg-surface border border-border-default rounded-sm p-8 h-full flex flex-col hover:border-border-accent transition-colors duration-300" data-id="element-439">
              <div className="mb-8" data-id="element-440">
                <h3 className="font-display text-2xl font-bold text-text-primary mb-2" data-id="element-441">
                  Digital Edition{' '}
                  <span className="text-text-muted font-normal text-lg" data-id="element-442">
                    (PDF)
                  </span>
                </h3>
                <div className="mt-6 mb-2" data-id="element-443">
                  <span className="font-display text-4xl font-bold text-text-primary" data-id="element-444">
                    {isLoading ? '$19' : formatPrice(19)}
                  </span>
                  <span className="font-ui text-sm text-text-muted ml-1 uppercase tracking-wider" data-id="element-445">
                    {currency.code}
                  </span>
                </div>
              </div>

              <div className="flex-grow" data-id="element-446">
                <ul className="space-y-4 mb-8" data-id="element-447">
                  {['170-page Memorial Book (PDF)', 'Instant Download', 'Lifetime Access'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-448">
                      <Check className="w-5 h-5 text-accent-primary flex-shrink-0 mt-0.5" data-id="element-449" />
                      <span className="font-body text-lg text-text-secondary" data-id="element-450">
                        {feature}
                      </span>
                    </li>)}
                </ul>
              </div>

              <Button href="https://www.mybetterlibrary.com/cart/42713853657165:1" variant="secondary" className="w-full" data-id="element-451">
                Get the Book
              </Button>
            </div>
          </FadeIn>

          {/* Card 3: Featured Bundle (Dominant) */}
          <FadeIn delay={0.4} className="h-full lg:-mt-8 lg:-mb-8 relative z-10" data-id="element-452">
            <div className="bg-elevated border-2 border-accent-primary rounded-sm p-8 md:p-10 h-full flex flex-col shadow-[0_8px_32px_rgba(201,168,76,0.15)] relative overflow-hidden" data-id="element-453">
              {/* Subtle inner glow */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-gold" data-id="element-454" />

              <div className="absolute top-0 right-0 bg-accent-primary text-base font-ui text-[10px] font-bold uppercase tracking-widest py-1.5 px-4 rounded-bl-sm" data-id="element-455">
                Most Popular
              </div>

              <div className="mb-8 mt-4" data-id="element-456">
                <h3 className="font-display text-3xl font-bold text-accent-primary mb-2 leading-tight" data-id="element-457">
                  The Complete Memorial Collection
                </h3>
                <div className="mt-6 mb-1" data-id="element-458">
                  <span className="font-display text-5xl font-bold text-text-primary" data-id="element-459">
                    {isLoading ? '$29' : formatPrice(29)}
                  </span>
                  <span className="font-ui text-sm text-text-muted ml-1 uppercase tracking-wider" data-id="element-460">
                    {currency.code}
                  </span>
                </div>
                <p className="font-ui text-xs font-semibold tracking-widest uppercase text-success mt-2" data-id="element-461">
                  Best Value — Save When Bundled
                </p>
              </div>

              <div className="flex-grow" data-id="element-462">
                <ul className="space-y-4 mb-8" data-id="element-463">
                  {['170-page Memorial Book (PDF)', 'Full-Length Audiobook', 'Exclusive Bonus Memorial PDF', 'Instant Access', 'Lifetime Access'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-464">
                      <Check className="w-5 h-5 text-accent-primary flex-shrink-0 mt-0.5" data-id="element-465" />
                      <span className="font-body text-lg text-text-primary font-medium" data-id="element-466">
                        {feature}
                      </span>
                    </li>)}
                </ul>
              </div>

              <div className="mt-auto" data-id="element-467">
                <Button href="https://www.mybetterlibrary.com/cart/42713853657165:1" variant="primary" className="w-full text-lg py-5" data-id="element-468">
                  Get the Full Collection
                </Button>
                <p className="font-ui text-[11px] text-accent-soft/80 text-center mt-4 tracking-widest uppercase" data-id="element-469">
                  Limited Digital Memorial Edition
                </p>
              </div>
            </div>
          </FadeIn>

          {/* Card 2: Audiobook Only */}
          <FadeIn delay={0.3} className="h-full" data-id="element-470">
            <div className="bg-surface border border-border-default rounded-sm p-8 h-full flex flex-col hover:border-border-accent transition-colors duration-300" data-id="element-471">
              <div className="mb-8" data-id="element-472">
                <h3 className="font-display text-2xl font-bold text-text-primary mb-2" data-id="element-473">
                  Audiobook Edition
                </h3>
                <div className="mt-6 mb-2" data-id="element-474">
                  <span className="font-display text-4xl font-bold text-text-primary" data-id="element-475">
                    {isLoading ? '$19' : formatPrice(19)}
                  </span>
                  <span className="font-ui text-sm text-text-muted ml-1 uppercase tracking-wider" data-id="element-476">
                    {currency.code}
                  </span>
                </div>
              </div>

              <div className="flex-grow" data-id="element-477">
                <ul className="space-y-4 mb-8" data-id="element-478">
                  {['Full-Length Audiobook', 'Instant Streaming & Download', 'Lifetime Access'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-479">
                      <Check className="w-5 h-5 text-accent-primary flex-shrink-0 mt-0.5" data-id="element-480" />
                      <span className="font-body text-lg text-text-secondary" data-id="element-481">
                        {feature}
                      </span>
                    </li>)}
                </ul>
              </div>

              <Button href="https://www.mybetterlibrary.com/cart/42713853657165:1" variant="secondary" className="w-full" data-id="element-482">
                Get the Audiobook
              </Button>
            </div>
          </FadeIn>
        </div>

        {/* Reassurance Line */}
        <FadeIn delay={0.5} data-id="element-483">
          <div className="flex items-center justify-center gap-2 text-center mb-16" data-id="element-484">
            <ShieldCheck className="w-4 h-4 text-text-muted" data-id="element-485" />
            <p className="font-ui text-xs md:text-sm text-text-muted tracking-wide" data-id="element-486">
              Secure checkout. Instant delivery. No shipping required.
            </p>
          </div>
        </FadeIn>

        {/* FAQ Dropdown */}
        <FadeIn delay={0.6} data-id="element-487">
          <div className="max-w-2xl mx-auto" data-id="element-488">
            <div className={`border border-border-default rounded-sm overflow-hidden transition-colors duration-300 ${faqOpen ? 'bg-elevated border-border-accent' : 'bg-surface hover:border-border-muted'}`} data-id="element-489">
              <button className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" onClick={() => setFaqOpen(!faqOpen)} data-id="element-490">
                <span className={`font-ui font-medium text-base md:text-lg ${faqOpen ? 'text-accent-primary' : 'text-text-primary'}`} data-id="element-491">
                  What happens after purchase?
                </span>
                <ChevronDown className={`w-5 h-5 text-text-muted transition-transform duration-300 ${faqOpen ? 'rotate-180 text-accent-primary' : ''}`} data-id="element-492" />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${faqOpen ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-493">
                <div className="px-6 pb-6 font-body text-lg text-text-secondary leading-relaxed border-t border-border-default/50 pt-4" data-id="element-494">
                  You receive immediate access via email to download your
                  selected edition and begin reading or listening right away.
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}